/**
 * Bench diagnostic plan calls for the customer portal (SIL-2246 U1): plan search and preview at approval, plan reads and
 * org-plan authoring. Thin wrappers over the Hono RPC client. Every route sits behind the server switch BENCH_PLANS_ENABLED:
 * while it is off, search and preview answer 409 `plans_disabled` and the plan CRUD answers 404 `plans_disabled`, and the UI
 * must degrade to the legacy approval (see isPlansDisabled).
 */
import type {
  BenchMandatorySetView,
  BenchOrgPlanCreateRequest,
  BenchOrgPlanVersionRequest,
  BenchPlanDetail,
  BenchPlanPreview,
  BenchPlanPreviewRequest,
  BenchPlanSearchResult,
} from '@siliconx/shared';
import { api, throwIfNotOk } from './api';

/** The closed error code of an API refusal (`{ error: { code } }`), or undefined. */
export function planErrorCode(err: unknown): string | undefined {
  const code = (err as { body?: { error?: { code?: unknown } } } | null)?.body?.error?.code;
  return typeof code === 'string' ? code : undefined;
}

export function planErrorStatus(err: unknown): number | undefined {
  return (err as { status?: number } | null)?.status;
}

/** True when the server switch is off: hide every plan feature and keep the legacy path. */
export function isPlansDisabled(err: unknown): boolean {
  return planErrorCode(err) === 'plans_disabled';
}

type Loose = (args: unknown) => Promise<Response>;

/** GET /plans?q=&code=. Without `code` there is no fit (the plans list); with it each result carries its fit for that machine. */
export async function searchBenchPlans(q: string, code?: string): Promise<BenchPlanSearchResult[]> {
  const get = api.api.client.bench.plans.$get as unknown as Loose;
  const query: Record<string, string> = {};
  if (q.trim() !== '') query.q = q.trim();
  if (code) query.code = code;
  const res = await get({ query });
  await throwIfNotOk(res);
  return ((await res.json()) as { plans: BenchPlanSearchResult[] }).plans;
}

export async function previewBenchPlan(code: string, body: BenchPlanPreviewRequest): Promise<BenchPlanPreview> {
  const post = api.api.client.bench.pairings[':userCode']['plan-preview'].$post as unknown as Loose;
  const res = await post({ param: { userCode: code }, json: body });
  await throwIfNotOk(res);
  return (await res.json()) as BenchPlanPreview;
}

export async function getBenchPlan(planId: string): Promise<BenchPlanDetail> {
  const get = api.api.client.bench.plans[':planId'].$get as unknown as Loose;
  const res = await get({ param: { planId } });
  await throwIfNotOk(res);
  return (await res.json()) as BenchPlanDetail;
}

export async function getBenchMandatorySet(): Promise<BenchMandatorySetView> {
  const get = api.api.client.bench['mandatory-set'].$get as unknown as Loose;
  const res = await get({});
  await throwIfNotOk(res);
  return (await res.json()) as BenchMandatorySetView;
}

export async function createBenchOrgPlan(body: BenchOrgPlanCreateRequest): Promise<{ planId: string; version: number }> {
  const post = api.api.client.bench.plans.$post as unknown as Loose;
  const res = await post({ json: body });
  await throwIfNotOk(res);
  return (await res.json()) as { planId: string; version: number };
}

export async function saveBenchOrgPlanVersion(
  planId: string,
  body: BenchOrgPlanVersionRequest,
): Promise<{ planId: string; version: number }> {
  const post = api.api.client.bench.plans[':planId'].versions.$post as unknown as Loose;
  const res = await post({ param: { planId }, json: body });
  await throwIfNotOk(res);
  return (await res.json()) as { planId: string; version: number };
}

export async function archiveBenchOrgPlan(planId: string, reason?: string): Promise<void> {
  const post = api.api.client.bench.plans[':planId'].archive.$post as unknown as Loose;
  const res = await post({ param: { planId }, json: reason ? { reason } : {} });
  await throwIfNotOk(res);
}
